import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  Onderneming,
  Ondernemingsvorm,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {
  debounceTime,
  filter,
  map,
  Observable, of,
  pairwise,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  take
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Router} from "@angular/router";

type OndernemingFormGroup = FormGroup<{
  werkzaamheden: FormControl<string | null>;
  naam: FormControl<string | null>;
  ondernemingsvorm: FormControl<string | null>;
  rsin: FormControl<string | null>;
  onttrekkingenOverig: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-algemeen',
    templateUrl: './algemeen.component.html',
    styleUrls: ['./algemeen.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDialogModule, CommonModule],
})
export class AlgemeenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    @Output() public openJaarstukken = new EventEmitter<number>();

    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private changeSubscriptions = new Subscription();
    public index = 0;
    public sstRelationId: any;
    public isPartner = false;
    public isDefAangifte = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        ondernemingen: new FormArray<OndernemingFormGroup>([]),
    });

    public optionsJaNee: SelectFormControlItem<boolean | null>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public ondernemingsvorm: SelectFormControlItem<string>[] = [
        { value: Ondernemingsvorm.EENMANSZAAK, label: 'Eenmanszaak' },
        { value: Ondernemingsvorm.VOF, label: 'Vennootschap onder firma (VOF)' },
        { value: Ondernemingsvorm.MAATSCHAP, label: 'Maatschap' },
        { value: Ondernemingsvorm.CV, label: 'Commanditaire vennootschap (CV) - nog niet beschikbaar', disabled: true },
    ];

    ngOnInit(): void {
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                this.isDefAangifte = entity.taxType === 'DA';
                this.sstRelationId = entity.sstRelationId;
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractInputForCalculation = () =>
            this.ondernemingen.getRawValue().map((item) => ({
                bedrag: item.onttrekkingenOverig,
            }));

        this.changeSubscriptions.add(
            this.ondernemingen.valueChanges
                .pipe(
                    debounceTime(500),
                    map(() => this.saveForm()),
                    map(() => extractInputForCalculation()),
                    startWith(extractInputForCalculation()),
                    pairwise(),
                    filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    public navigateToSubPage(i: number): void {
        this.dataService.indexOnderneming.next(i);
        this.openJaarstukken.emit(i);
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
        }
    }

    private resetAndPatchForms(details: any): void {
        this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen || []);
    }

    get ondernemingen(): FormArray<OndernemingFormGroup> {
        return this.formGroup.controls.ondernemingen;
    }

    addRij(): void {
        this.ondernemingen.push(this.createFormGroup(), { emitEvent: false });
        this.saveForm();
    }

    verwijderRij(index: number): void {
        this.ondernemingen.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(item?: Onderneming): FormGroup {
        return new FormGroup({
            werkzaamheden: new FormControl<string | null>(
                item?.werkzaamheden
                    ?.replace(/[^a-zA-Z0-9\s]/g, '')
                    .replace(/\s+/g, ' ')
                    .trim() ?? null,
                { validators: [Validators.required] },
            ),
            naam: new FormControl<string | null>(
                item?.naam
                    ?.replace(/[^a-zA-Z0-9\s]/g, '')
                    .replace(/\s+/g, ' ')
                    .trim() ?? null,
                { validators: [Validators.required] },
            ),
            ondernemingsvorm: new FormControl<string | null>(item?.ondernemingsvorm ?? null, { validators: [Validators.required] }),
            rsin: new FormControl<string | null>(item?.rsin ?? null, {
                validators: [Validators.pattern(/^\d{9}$/)],
            }),
            onttrekkingenOverig: new FormControl<number | null>(item?.onttrekkingenOverig?.[0]?.bedrag ?? null),
        });
    }

    private updateForm(ondernemingen: Onderneming[]): void {
        let lijst = ondernemingen || [];
        const formArray = this.ondernemingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const formGroup = formArray.at(i) as FormGroup;
                formGroup.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        const formOndernemingen = this.formGroup.getRawValue().ondernemingen;

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            const update = (aangifte: Aangifte) => {
                let winstUitOnderneming = aangifte.box1?.winstUitOnderneming;
                let ondernemingen = formOndernemingen.map((formOnd, idx) => {
                    const existing: Onderneming = winstUitOnderneming?.ondernemingen?.[idx] ?? ({} as Onderneming);
                    const { onttrekkingenOverig, ...restFormOnd } = formOnd;

                    const mappedOnttrekkingenOverig =
                        onttrekkingenOverig != null
                            ? [{ omschrijving: existing.onttrekkingenOverig?.[0]?.omschrijving ?? 'Winst', bedrag: onttrekkingenOverig }]
                            : undefined;

                    const mappedNietAftrekbareKosten =
                        onttrekkingenOverig != null
                            ? [{ omschrijving: existing.nietAftrekbareKosten?.[0]?.omschrijving ?? 'Winst', bedrag: onttrekkingenOverig }]
                            : undefined;

                    return {
                        ...existing,
                        ...restFormOnd,
                        onttrekkingenOverig: this.isDefAangifte ? existing.onttrekkingenOverig : mappedOnttrekkingenOverig,
                        onttrekkingPriveGebruikAuto: this.isDefAangifte ? existing.onttrekkingPriveGebruikAuto : undefined,
                        onttrekkingPriveGebruikWoning: this.isDefAangifte ? existing.onttrekkingPriveGebruikWoning : undefined,
                        stortingenOverig: this.isDefAangifte ? existing.stortingenOverig : undefined,
                        stortingOG: this.isDefAangifte ? existing.stortingOG : undefined,
                        resultaatSamenwerking: this.isDefAangifte ? existing.resultaatSamenwerking : undefined,
                        nietAftrekbareKosten: this.isDefAangifte ? existing.nietAftrekbareKosten : mappedNietAftrekbareKosten,
                    };
                });
                // If the list is empty, remove the entire winstUitOnderneming object
                if (!ondernemingen.length) {
                    return {
                        ...aangifte,
                        box1: {
                            ...aangifte.box1,
                            winstUitOnderneming: undefined,
                        },
                    };
                } else {
                    // Only assign ondernemingen if not empty and merged with existing objects
                    return {
                        ...aangifte,
                        box1: {
                            ...aangifte.box1,
                            winstUitOnderneming: {
                                ...winstUitOnderneming,
                                ondernemingen: ondernemingen as any, // merged with existing, so safe
                            },
                        },
                    };
                }
            };

            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
                  }
                : update(currentEntity.aangifte);

            return {
                ...currentEntity,
                aangifte: updatedAangifte,
            } as IncomeTaxEntity;
        });
    }

    protected readonly Ondernemingsvorm = Ondernemingsvorm;
}
